import type { ReactNode } from 'react';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import {
  NavbarWithCtaButton,
  SitemapLinksFooterSection,
} from '@flotiq/flotiq-flowbite';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import {
  authButtonsData,
  generateRedirectUrl,
  getTestProps,
} from '../../lib/helpers';
import { detectLanguage } from '../../lib/language';

import Button from '../../components/Button/Button';

import { useFooter } from '../../hooks/navigation/useFooter';
import { useTopNav } from '../../hooks/navigation/useTopNav';
import useDarkMode from '../../hooks/useDarkMode';

interface AuthenticationProps {
  /**
   * Header text
   */
  headerText?: string;
  /**
   * Paragraph text
   */
  paragraphText?: ReactNode;
  /**
   * If buttons to authenticate should be visible
   */
  authButtons?: boolean;
  /**
   * Children
   */
  children?: ReactNode;
  /**
   * Information section next to the login form
   */
  infoSection?: ReactNode;
  /**
   * Authentication test id
   */
  testId?: string;
}

const Authentication = ({
  headerText = '',
  paragraphText = '',
  authButtons = false,
  children,
  infoSection,
  testId = '',
}: AuthenticationProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [, , { removeItem: removeUser }] = useLocalStorageState('cms.user');
  const { lang } = useParams();
  const targetLang = lang ?? detectLanguage();

  const { footer } = useFooter();
  const { topNav } = useTopNav();

  useDarkMode();

  const connectWith = useCallback(
    (url: string) => {
      removeUser();

      const searchParamsString = searchParams.toString();
      const loginUrl =
        `/${targetLang}/login` +
        (searchParamsString ? `?${searchParamsString}` : '');

      window.location.replace(
        `${url}?redirectUri=${generateRedirectUrl(loginUrl, true)}${
          searchParams.has('plan') ? `&plan=${searchParams.get('plan')}` : ''
        }`,
      );
    },
    [removeUser, searchParams, targetLang],
  );

  return (
    <div
      id="authentication-section"
      className="flex flex-col min-h-screen dark:text-white max-h-screen overflow-y-overlay scrollbar-sm"
    >
      <NavbarWithCtaButton {...topNav} />

      <div
        className={twMerge(
          'flex flex-col lg:flex-row px-5 xl:px-0 w-full lg:px-8 max-w-lg lg:max-w-6xl 2xl:max-w-7xl',
          'mx-auto mb-10 pt-10 lg:pt-12 3xl:pt-20 items-start',
        )}
      >
        {infoSection && (
          <div className="lg:basis-1/2 max-w-full min-w-0 mt-10 lg:mt-0">
            {infoSection}
          </div>
        )}

        <div
          className={twMerge(
            'flex items-center w-full mx-auto max-w-[480px]',
            'dark:bg-slate-900 order-first lg:order-0',
            infoSection && 'lg:mr-0 lg:ml-auto lg:basis-5/12 xl:basis-2/5',
          )}
          {...getTestProps(testId, 'container')}
        >
          <div className="w-full flex flex-col items-center">
            <h1 className="text-3xl lg:text-4xl 2xl:text-5xl leading-normal lg:leading-snug 2xl:leading-normal">
              {headerText}
            </h1>

            {paragraphText && (
              <p className={'text-center 2xl:text-lg mt-3'}>{paragraphText}</p>
            )}

            {authButtons && (
              <div className="w-full flex flex-col items-center mt-5">
                <div className="w-full space-y-4">
                  {authButtonsData.map((auth) => (
                    <Button
                      key={auth.name}
                      id={`button_auth_${auth.name.toLowerCase()}`}
                      buttonSize="lg"
                      iconImage={auth.logo}
                      iconPosition={'start'}
                      buttonColor={'grayBordered'}
                      onClick={() => connectWith(auth.url)}
                      additionalClasses="w-full justify-center"
                    >
                      {t('Global.ContinueWith')} {auth.name}
                    </Button>
                  ))}
                </div>
                <div className="w-full h-full relative my-8 2xl:my-12">
                  <div className="w-full h-px bg-slate-400/50 select-none" />
                  <span
                    className="absolute top-0 left-1/2 flex items-center justify-center w-16
                                bg-white dark:bg-slate-900 -translate-x-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {t('Global.Or')}
                  </span>
                </div>

                {children}
              </div>
            )}

            {!authButtons && <div className={'w-full mt-8'}>{children}</div>}
          </div>
        </div>
      </div>

      <SitemapLinksFooterSection {...footer} />
    </div>
  );
};

export default Authentication;
